import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type Settings as SettingsData, type User } from '../api';
import { isOwner, useAuth } from '../auth';
import { useSettings } from '../queries';
import { describeLastRun } from '../components/Market';
import { useMarketStatus } from '../market';
import { Badge, Button, Card, Checkbox, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, number, when } from '../ui';

interface UserRow extends User {
  disabled: boolean;
  lastLoginAt: string | null;
}

export function Settings() {
  const { user } = useAuth();
  return (
    <>
      <PageHeader title="Settings" />
      <div className="space-y-4">
        {isOwner(user) && <InventorySettings />}
        {isOwner(user) && <PriceLevelsSettings />}
        {isOwner(user) && <MarketSettings />}
        <PasswordCard />
        {isOwner(user) && <Users />}
      </div>
    </>
  );
}

function InventorySettings() {
  const qc = useQueryClient();
  const settings = useSettings();
  const save = useMutation({
    mutationFn: (patch: Partial<SettingsData>) => api('/api/v1/settings', { method: 'PUT', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  if (settings.isPending) return <Spinner />;
  return (
    <Card title="Inventory">
      <div className="max-w-xl space-y-3">
        <Field
          label="Where new stock goes"
          hint={
            settings.data?.inventoryMode === 'consolidated'
              ? 'Each item has one home. New stock of an item you already have goes to its home location unless you choose another.'
              : 'Stock goes wherever you put it, so one item can be in several places. Nothing has to be moved to tidy up, and picking visits each location.'
          }
        >
          <Select value={settings.data?.inventoryMode} onChange={(e) => save.mutate({ inventoryMode: e.target.value as SettingsData['inventoryMode'] })}>
            <option value="unconsolidated">Anywhere (unconsolidated)</option>
            <option value="consolidated">One home per item (consolidated)</option>
          </Select>
        </Field>
        <Field
          label="Postage on purchases"
          hint={
            settings.data?.postageTreatment === 'spread'
              ? 'Postage and insurance are added to what the parts cost, in proportion to their value, so each part carries a share.'
              : 'Postage and insurance are a running cost of the business, not part of what a part cost. A part costs what it was priced at, so £3.50 of parts with £4.69 postage costs £3.50.'
          }
        >
          <Select value={settings.data?.postageTreatment ?? 'spread'} onChange={(e) => save.mutate({ postageTreatment: e.target.value as SettingsData['postageTreatment'] })}>
            <option value="spread">Spread it over the parts by value (recommended)</option>
            <option value="absorb">Absorb it (parts cost their price)</option>
          </Select>
        </Field>
        <Field label="Prices" hint="When BrickLink's figures arrive for a lot with no price, apply this plan. Prices you have set, and locked prices, are never changed. The Pricing page lists anything losing money.">
          <Checkbox label="Price newly added lots that have no price automatically" checked={settings.data?.autoPrice ?? true} onChange={(v) => save.mutate({ autoPrice: v })} />
        </Field>
        <Field label="Default pricing plan">
          <Select value={settings.data?.autoPricePlan ?? 'market'} onChange={(e) => save.mutate({ autoPricePlan: e.target.value as SettingsData['autoPricePlan'] })}>
            <option value="market">In line with the market</option><option value="quick">Quick sale</option><option value="patient">Patient</option><option value="floor">Floor</option><option value="cost_plus">Cost plus 50%</option><option value="breakeven">Break even (your cost)</option>
          </Select>
        </Field>
        <Field label="Selling at a loss" hint="Where the market price is under what the stock cost, price the lot at its cost instead (no profit, no loss). Applies to automatic prices; a price you set by hand is yours.">
          <Checkbox label="Never price automatically below cost" checked={settings.data?.neverBelowCost ?? true} onChange={(v) => save.mutate({ neverBelowCost: v })} />
        </Field>
        <ErrorMessage error={save.error} />
        {save.isSuccess && <Notice tone="green">Saved.</Notice>}
        <p className="text-xs text-slate-500">Changing these affects stock you add or receive from now on. Existing stock keeps the cost it has.</p>
      </div>
    </Card>
  );
}

function MarketSettings() {
  const qc = useQueryClient();
  const settings = useSettings();
  const status = useMarketStatus();
  const save = useMutation({
    mutationFn: (patch: Partial<SettingsData>) => api('/api/v1/settings', { method: 'PUT', body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['market-status'] });
      void qc.invalidateQueries({ queryKey: ['lots'] });
      void qc.invalidateQueries({ queryKey: ['lot'] });
    },
  });
  if (settings.isPending) return <Spinner />;
  const s = status.data;
  return (
    <Card title="BrickLink market figures">
      <div className="max-w-xl space-y-3">
        <Field
          label="Whose sales to count"
          hint="Sell-through and months of stock count the sales and listings of every BrickLink seller in this region, not just yours. Worldwide is the safest. Changing it starts a fresh set of figures that fills in over the coming nights; the other regions' figures are kept. Check a UK or Europe figure against BrickLink's own price guide before you rely on it."
        >
          <Select value={settings.data?.sellThroughRegion ?? 'worldwide'} onChange={(e) => save.mutate({ sellThroughRegion: e.target.value as SettingsData['sellThroughRegion'] })}>
            <option value="worldwide">Worldwide</option>
            <option value="uk">United Kingdom</option>
            <option value="europe">Europe</option>
          </Select>
        </Field>
        <ErrorMessage error={save.error} />
        {save.isSuccess && <Notice tone="green">Saved.</Notice>}
        {s && (
          <div className="space-y-1 text-sm text-slate-600">
            <p>
              {number(s.withFigures)} of {number(s.lotsInStock)} lots in stock have figures for this region
              {s.unavailable > 0 && ` (BrickLink has no price guide for ${number(s.unavailable)})`}.
            </p>
            <p>{describeLastRun(s.lastRun)}</p>
            <p className="text-xs text-slate-500">
              The overnight refresh makes at most {number(s.runLimit)} requests a night and never touches the last {number(s.reserve)} of BrickLink&apos;s {number(s.requestsToday.cap)} a day, which
              stay free for orders and stock. Today {number(s.requestsToday.used)} have been used.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => api('/api/v1/auth/password', { body: { current, next } }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });
  return (
    <Card title="Change your password">
      <form
        className="flex max-w-xl flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
      >
        <Field label="Current password">
          <Input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="New password" hint="At least 12 characters. Other devices are signed out.">
          <Input type="password" autoComplete="new-password" required minLength={12} value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Button type="submit" disabled={change.isPending || next.length < 12}>
          Change
        </Button>
        <div className="basis-full">
          <ErrorMessage error={change.error} />
          {change.isSuccess && <Notice tone="green">Password changed.</Notice>}
        </div>
      </form>
    </Card>
  );
}

function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: UserRow[] }>('/api/v1/users').then((r) => r.users) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] });
  const [form, setForm] = useState({ email: '', name: '', role: 'staff', password: '' });

  const create = useMutation({
    mutationFn: () => api('/api/v1/users', { body: form }),
    onSuccess: () => {
      setForm({ email: '', name: '', role: 'staff', password: '' });
      void refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, disabled }: { id: number; disabled: boolean }) => api(`/api/v1/users/${id}/disabled`, { body: { disabled } }),
    onSuccess: refresh,
  });

  return (
    <Card title="People">
      <div className="space-y-4">
        <ul className="divide-y divide-slate-100 text-sm">
          {users.data?.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <div>
                <span className="font-medium">{u.name}</span> <span className="text-slate-500">{u.email}</span>{' '}
                <Badge tone={u.role === 'owner' ? 'blue' : 'slate'}>{u.role}</Badge> {u.disabled && <Badge tone="red">disabled</Badge>}
                <div className="text-xs text-slate-500">Last signed in {when(u.lastLoginAt)}</div>
              </div>
              {u.id !== me?.id && (
                <Button small variant="secondary" onClick={() => toggle.mutate({ id: u.id, disabled: !u.disabled })}>
                  {u.disabled ? 'Enable' : 'Disable'}
                </Button>
              )}
            </li>
          ))}
        </ul>
        <ErrorMessage error={toggle.error} />
        <form
          className="grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Name">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Role" hint="Pickers see stock but not costs.">
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="staff">Staff</option>
              <option value="picker">Picker</option>
              <option value="owner">Owner</option>
            </Select>
          </Field>
          <Field label="Password" hint="12+ characters">
            <Input type="password" autoComplete="new-password" required minLength={12} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <div className="flex items-end">
            <Button type="submit" disabled={create.isPending}>
              Add person
            </Button>
          </div>
          <div className="sm:col-span-2 lg:col-span-5">
            <ErrorMessage error={create.error} />
          </div>
        </form>
      </div>
    </Card>
  );
}


interface LevelRow {
  id: number;
  name: string;
  percent: string;
  active: boolean;
}

/** Price levels: named percentages off the base price, used when a price is offered (a direct order, say). */
function PriceLevelsSettings() {
  const qc = useQueryClient();
  const levels = useQuery({ queryKey: ['price-levels-all'], queryFn: () => api<{ levels: LevelRow[] }>('/api/v1/price-levels').then((r) => r.levels) });
  const [name, setName] = useState('');
  const [percent, setPercent] = useState('-10');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['price-levels-all'] });
    void qc.invalidateQueries({ queryKey: ['price-levels'] });
  };
  const add = useMutation({
    mutationFn: () => api('/api/v1/price-levels', { body: { name, percent } }),
    onSuccess: () => {
      setName('');
      refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (l: LevelRow) => api(`/api/v1/price-levels/${l.id}`, { method: 'PATCH', body: { name: l.name, percent: l.percent, active: !l.active } }),
    onSuccess: refresh,
  });
  return (
    <Card title="Price levels">
      <div className="max-w-xl space-y-3">
        <p className="text-sm text-slate-600">A price level is a named percentage off (or on) each lot's price, such as <em>Show price, -10%</em> or <em>Wholesale, -30%</em>. Choose one on a direct order to fill in the prices. The lot's own price stays the one price on the lot and on BrickLink.</p>
        {levels.data && levels.data.length > 0 && (
          <ul className="divide-y divide-slate-100 text-sm">
            {levels.data.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 py-1.5">
                <span className={l.active ? '' : 'text-slate-400'}>
                  {l.name} <Badge>{Number(l.percent) > 0 ? '+' : ''}{Number(l.percent)}%</Badge>
                </span>
                <Button variant="ghost" small onClick={() => toggle.mutate(l)}>
                  {l.active ? 'Switch off' : 'Switch on'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Show price" />
          </Field>
          <Field label="Percent" hint="-10 is ten percent under">
            <Input className="w-24" value={percent} onChange={(e) => setPercent(e.target.value)} />
          </Field>
          <Button disabled={!name.trim() || add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
        </div>
        <ErrorMessage error={add.error ?? toggle.error} />
      </div>
    </Card>
  );
}
